import PdfLayout from '../PdfLayout';
import CustomTextWidget from './CustomTextWidget';
import formatPricingName from './formatPricingName';
import moment from 'moment';
import { Utils } from '../../services';
import i18n from '../../locales/i18n';
import {
    PREVIEW_PEOPLE,
    PREVIEW_PRICE,
    PREVIEW_CURRENCY,
    PREVIEW_PRICING_NAME,
    PREVIEW_TICKET_ID,
    PREVIEW_VDR_AUTH,
    PREVIEW_VAT,
    buildPreviewWindows,
    formatPreviewPrice,
    previewLocalized,
} from '../../services/pdfLayoutPreviewSample';

/**
 * Class representing a PDF layout widget showing
 * an information about the ticket.
 */
class TicketInfoWidget extends CustomTextWidget {

    constructor(properties) {
        super({
            ...(TicketInfoWidget.getDefaultOptions()),
            ...properties,
        });

        this.type    = PdfLayout.TEXT;
        this.subType = PdfLayout.TICKET_INFO;

        if (['activated_at', 'first_window', 'last_window', 'first_window_last_window'].includes(this.options?.field))
            this.options.date_format = this.options.date_format ?? 'lll';
    }

    /**
     * Get this widget default options
     */
    static getDefaultOptions() {
        return {
            position: { x: 2, y: 2, z: 0 },
            dimensions : { width: 5, height: 0.75 },
            transform: { rotation: 0 },
            options: {
                field: 'ticket_id',
                style: {
                    color: '#000000',
                    fontFamily: "Helvetica",
                    fontSize: 14,
                    lineHeight: 1,
                    textAlign: 'left',
                    textDecoration: 'none',
                    textStrokeWidth: 0,
                    textStrokeColor: '#000000',
                    backgroundColor: '#FFFFFF00',
                    borderStyle: 'solid',
                    borderWidth: 0,
                    borderColor: '#000000',
                    borderRadius: 0,
                    padding: {
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0,
                    }
                },
            }
        };
    }

    /**
     * Return the list of options this widget handles
     */
    getHandledOptions() {
        const handledOptions = super.getHandledOptions().concat([
            PdfLayout.OPTION_TICKET_FIELD
        ]).filter(o => o !== PdfLayout.OPTION_TEXT);

        if (['activated_at', 'first_window', 'last_window', 'first_window_last_window'].includes(this.options?.field)) {
            handledOptions.push(PdfLayout.OPTION_DATE_FORMAT);
            handledOptions.push(PdfLayout.OPTION_DATE_OFFSET);
        }

        if (['vat'].includes(this.options?.field)) {
            handledOptions.push(PdfLayout.OPTION_VAT_FORMAT);
        }

        if (this.options?.field === 'activated_pricing_name') {
            handledOptions.push(PdfLayout.OPTION_SHOW_PEOPLE);
        }

        return handledOptions;
    }

    formatPreviewDate(value, lang, momentFormat) {
        let m = moment(value).locale(Utils.pdfMomentLocale(lang));
        if (this.options?.date_offset)
            m = m.add(parseInt(this.options.date_offset, 10), 'days');
        return m.format(momentFormat);
    }

    previewValidityWindows(props) {
        return buildPreviewWindows({
            tickettype: props.tickettype,
            screening: props.screening,
            programmation: props.programmation,
        });
    }

    /**
     * Return this widget preview as JSX string
     *
     * @param {Object} props with at least the following properties:
     *                 - {Bool} inEdition
     *                 - {String} lang
     *                 - {Object} setting
     */
    getPreviewText(props) {
        const { pricing } = props;
        let lang = props?.lang.length > 0 ? props.lang : 'fr';
        const momentFormat = this.options?.date_format ?? 'lll';

        switch (this.options.field) {
            case 'ticket_id':
                return [
                    '<span class="tkt-ticketid_ticket">Ticket</span>',
                    '<span class="tkt-ticketid_id">ID</span>',
                    // Match Engine TicketInfoWidget: colon + space (not padding before ":").
                    '<span class="tkt-ticketid_sep">: </span>',
                    `<span class="tkt-ticketid_ticket_value">${PREVIEW_VDR_AUTH.vdr_auth_serial}</span>`,
                    '-',
                    `<span class="tkt-ticketid_id_value">${PREVIEW_VDR_AUTH.vdr_auth_key}</span>`
                ].join('');
            case 'ticket_id_only':
                return [
                    `<span class="tkt-ticketid_ticket_value">${PREVIEW_VDR_AUTH.vdr_auth_serial}</span>`,
                    '-',
                    `<span class="tkt-ticketid_id_value">${PREVIEW_VDR_AUTH.vdr_auth_key}</span>`
                ].join('');
            case 'ticket_id_festivalscope':
                return [
                    `<span class="tkt-ticketid_ticket_value">${PREVIEW_VDR_AUTH.vdr_auth_serial}</span>`,
                    `<span class="tkt-ticketid_id_value">${PREVIEW_VDR_AUTH.vdr_auth_key}</span>`
                ].join('');
            case 'uuid':
                return PREVIEW_TICKET_ID;
            case 'activated_at':
                return this.formatPreviewDate(Date.now(), lang, momentFormat);
            case 'activated_pricing_name': {
                const name = Utils.localized_or_fallback(
                    pricing?.name,
                    lang,
                    previewLocalized(PREVIEW_PRICING_NAME, lang)
                );
                const people = props.ticket?.type?.people
                    ?? props.tickettype?.people
                    ?? PREVIEW_PEOPLE;
                return formatPricingName(name, people, this.options?.show_people);
            }
            case 'activated_pricing_description':
                return Utils.localized_or_fallback(pricing?.description, lang, '');
            case 'activated_pricing_price_offered_if_free':
            case 'activated_pricing_price_free_if_free':
            case 'activated_pricing_price':
            case 'activated_pricing_price_even_if_free': {
                let price = PREVIEW_PRICE;
                let currency = Utils.currency() || PREVIEW_CURRENCY;
                if (pricing?.price && typeof pricing.price === 'object') {
                    const keys = Object.keys(pricing.price);
                    if (keys.length) {
                        currency = keys[0];
                        price = pricing.price[currency];
                    }
                }
                if (price === 0) {
                    if (this.options.field === 'activated_pricing_price_even_if_free')
                        return formatPreviewPrice(0, currency, lang);
                    if (this.options.field === 'activated_pricing_price_offered_if_free')
                        return i18n.t('layouts.ticket_preview_offered', { lng: lang });
                    if (this.options.field === 'activated_pricing_price_free_if_free')
                        return i18n.t('layouts.ticket_preview_free', { lng: lang });
                    return null;
                }
                return formatPreviewPrice(price, currency, lang);
            }
            case 'vat':
                let value = pricing ? pricing.VAT : PREVIEW_VAT;
                if (!value)
                    return '';
                return (this.options?.vat_format === 'value_only') ? value + '%'  : i18n.t("layouts.vat_format_full", { value , lng: lang });
            case 'first_window': {
                const windows = this.previewValidityWindows(props);
                if (!windows.length)
                    return '';
                return this.formatPreviewDate(windows[0].start_at, lang, momentFormat);
            }
            case 'last_window': {
                const windows = this.previewValidityWindows(props);
                if (!windows.length)
                    return '';
                return this.formatPreviewDate(windows[windows.length - 1].stop_at, lang, momentFormat);
            }
            case 'first_window_last_window': {
                const windows = this.previewValidityWindows(props);
                if (!windows.length)
                    return '';
                return [
                    this.formatPreviewDate(windows[0].start_at, lang, momentFormat),
                    this.formatPreviewDate(windows[windows.length - 1].stop_at, lang, momentFormat),
                ].join(' - ');
            }
            default:
                return '...';
        }
    }

    /**
     * Return the icon to use on the editor button
     */
    getIcon() {
        return 'tag';
    }
}

export default TicketInfoWidget;
